import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../../../components/ui/button';
import { Plus, AlertTriangle, AlertCircle, Clock, Info, Eye, RefreshCw, Search, Timer, CheckCircle } from 'lucide-react';
import axios from 'axios';

interface Ticket {
  id: number;
  ticketNumber: string;
  title: string;
  description: string;
  categoryName: string | null;
  priority: string;
  status: string;
  location: string | null;
  classroomName: string | null;
  equipmentName: string | null;
  reportedByName: string;
  assignedToName: string | null;
  assignedAt: string | null;
  resolvedAt: string | null;
  createdAt: string;
  updatedAt: string;
}

interface TicketComment {
  id: number;
  userId: number;
  userName: string;
  commentType: string;
  content: string;
  isInternal: boolean;
  oldStatus: string | null;
  newStatus: string | null;
  createdAt: string;
}

interface TicketDetail extends Ticket {
  reportedByName: string;
  assignedToName: string | null;
  closedByName: string | null;
  comments: TicketComment[];
}

const priorityConfig: Record<string, { color: string; icon: React.ReactNode }> = {
  P0: { color: 'bg-red-100 text-red-800', icon: <AlertTriangle className="w-3 h-3" /> },
  P1: { color: 'bg-orange-100 text-orange-800', icon: <AlertCircle className="w-3 h-3" /> },
  P2: { color: 'bg-yellow-100 text-yellow-800', icon: <Clock className="w-3 h-3" /> },
  P3: { color: 'bg-green-100 text-green-800', icon: <Info className="w-3 h-3" /> },
};

const statusConfig: Record<string, { label: string; color: string }> = {
  abierto: { label: 'Abierto', color: 'bg-blue-100 text-blue-800' },
  en_curso: { label: 'En Curso', color: 'bg-yellow-100 text-yellow-800' },
  en_espera: { label: 'En Espera', color: 'bg-gray-100 text-gray-800' },
  resuelto: { label: 'Resuelto', color: 'bg-green-100 text-green-800' },
  cerrado: { label: 'Cerrado', color: 'bg-slate-100 text-slate-800' },
};

export default function MisTickets() {
  const navigate = useNavigate();
  const [tickets, setTickets] = useState<Ticket[]>([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('all');
  const [selectedTicket, setSelectedTicket] = useState<TicketDetail | null>(null);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [searchText, setSearchText] = useState('');

  useEffect(() => {
    fetchTickets();
  }, []);

  const fetchTickets = async () => {
    setLoading(true);
    try {
      const response = await axios.get('/api/tickets');
      setTickets(response.data);
    } catch (err) {
      console.error('Error fetching tickets:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchTicketDetail = async (ticketId: number) => {
    setLoadingDetail(true);
    try {
      const response = await axios.get(`/api/tickets/${ticketId}`);
      setSelectedTicket(response.data);
    } catch (err) {
      console.error('Error fetching ticket detail:', err);
    } finally {
      setLoadingDetail(false);
    }
  };

  const calculateTimeDiff = (startDate: string, endDate: string | null): { minutes: number; formatted: string } => {
    const start = new Date(startDate);
    const end = endDate ? new Date(endDate) : new Date();
    const diffMs = end.getTime() - start.getTime();
    const diffMinutes = Math.floor(diffMs / 60000);
    
    if (diffMinutes < 60) {
      return { minutes: diffMinutes, formatted: `${diffMinutes} min` };
    } else if (diffMinutes < 1440) {
      const hours = Math.floor(diffMinutes / 60);
      const mins = diffMinutes % 60;
      return { minutes: diffMinutes, formatted: `${hours}h ${mins}m` };
    } else {
      const days = Math.floor(diffMinutes / 1440);
      const hours = Math.floor((diffMinutes % 1440) / 60);
      return { minutes: diffMinutes, formatted: `${days}d ${hours}h` };
    }
  };

  const getAssignmentTime = (ticket: Ticket): { minutes: number; formatted: string } | null => {
    if (!ticket.assignedAt) return null;
    return calculateTimeDiff(ticket.createdAt, ticket.assignedAt);
  };

  const getResolutionTime = (ticket: Ticket): { minutes: number; formatted: string } | null => {
    if (!ticket.resolvedAt) return null;
    return calculateTimeDiff(ticket.createdAt, ticket.resolvedAt);
  };

  const filteredTickets = tickets
    .filter(t => statusFilter === 'all' || t.status === statusFilter)
    .filter(t => {
      if (!searchText.trim()) return true;
      const search = searchText.toLowerCase();
      return (
        t.ticketNumber.toLowerCase().includes(search) ||
        t.title.toLowerCase().includes(search) ||
        (t.categoryName && t.categoryName.toLowerCase().includes(search)) ||
        (t.location && t.location.toLowerCase().includes(search)) ||
        (t.assignedToName && t.assignedToName.toLowerCase().includes(search))
      );
    });

  const calculateAverages = () => {
    const ticketsWithTA = filteredTickets.filter(t => t.assignedAt);
    const ticketsWithTR = filteredTickets.filter(t => t.resolvedAt);

    const avgTA = ticketsWithTA.length > 0
      ? ticketsWithTA.reduce((sum, t) => sum + (getAssignmentTime(t)?.minutes || 0), 0) / ticketsWithTA.length
      : 0;

    const avgTR = ticketsWithTR.length > 0
      ? ticketsWithTR.reduce((sum, t) => sum + (getResolutionTime(t)?.minutes || 0), 0) / ticketsWithTR.length
      : 0;

    const formatAvg = (minutes: number): string => {
      if (minutes === 0) return '-';
      if (minutes < 60) return `${Math.round(minutes)} min`;
      if (minutes < 1440) {
        const hours = Math.floor(minutes / 60);
        const mins = Math.round(minutes % 60);
        return `${hours}h ${mins}m`;
      }
      const days = Math.floor(minutes / 1440);
      const hours = Math.round((minutes % 1440) / 60);
      return `${days}d ${hours}h`;
    };

    return {
      avgTA: formatAvg(avgTA),
      avgTR: formatAvg(avgTR),
      countTA: ticketsWithTA.length,
      countTR: ticketsWithTR.length,
    };
  };

  const averages = calculateAverages();

  const formatDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleDateString('es-MX', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const formatCommentDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleDateString('es-MX', {
      day: '2-digit',
      month: 'short',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  return (
    <div className="p-6">
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-2xl font-bold">Mis Tickets</h1>
          <p className="text-gray-500">Historial de reportes y su estado actual</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={fetchTickets}>
            <RefreshCw className="w-4 h-4 mr-2" />
            Actualizar
          </Button>
          <Button onClick={() => navigate('/app/tickets/nuevo')}>
            <Plus className="w-4 h-4 mr-2" />
            Nuevo Reporte
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <div className="bg-blue-50 rounded-lg p-4 border border-blue-100">
          <div className="flex items-center gap-2 mb-1">
            <Timer className="w-4 h-4 text-blue-600" />
            <span className="text-sm font-medium text-blue-700">Promedio TA</span>
          </div>
          <div className="text-2xl font-bold text-blue-800">{averages.avgTA}</div>
          <div className="text-xs text-blue-600">{averages.countTA} tickets asignados</div>
        </div>
        <div className="bg-green-50 rounded-lg p-4 border border-green-100">
          <div className="flex items-center gap-2 mb-1">
            <CheckCircle className="w-4 h-4 text-green-600" />
            <span className="text-sm font-medium text-green-700">Promedio TR</span>
          </div>
          <div className="text-2xl font-bold text-green-800">{averages.avgTR}</div>
          <div className="text-xs text-green-600">{averages.countTR} tickets resueltos</div>
        </div>
        <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
          <div className="text-sm font-medium text-gray-600 mb-1">Total Tickets</div>
          <div className="text-2xl font-bold text-gray-800">{filteredTickets.length}</div>
          <div className="text-xs text-gray-500">de {tickets.length} totales</div>
        </div>
        <div className="bg-yellow-50 rounded-lg p-4 border border-yellow-100">
          <div className="text-sm font-medium text-yellow-700 mb-1">Pendientes</div>
          <div className="text-2xl font-bold text-yellow-800">
            {tickets.filter(t => !['resuelto', 'cerrado'].includes(t.status)).length}
          </div>
          <div className="text-xs text-yellow-600">sin resolver</div>
        </div>
      </div>

      <div className="mb-4 flex flex-col sm:flex-row gap-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
          <input
            type="text"
            placeholder="Buscar por número, título, categoría, ubicación..."
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            className="w-full pl-10 pr-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          />
        </div>
        <div className="flex gap-2 flex-wrap">
          <button
            onClick={() => setStatusFilter('all')}
            className={`px-3 py-1 rounded-full text-sm ${
              statusFilter === 'all' ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
            }`}
          >
            Todos ({tickets.length})
          </button>
          {Object.entries(statusConfig).map(([key, { label }]) => {
            const count = tickets.filter(t => t.status === key).length;
            return (
              <button
                key={key}
                onClick={() => setStatusFilter(key)}
                className={`px-3 py-1 rounded-full text-sm ${
                  statusFilter === key ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
              >
                {label} ({count})
              </button>
            );
          })}
        </div>
      </div>

      {loading ? (
        <div className="text-center py-12 text-gray-500">Cargando tickets...</div>
      ) : filteredTickets.length === 0 ? (
        <div className="bg-white rounded-lg border p-12 text-center">
          <div className="text-gray-400 mb-4">
            <AlertCircle className="w-12 h-12 mx-auto" />
          </div>
          <h3 className="text-lg font-medium text-gray-600 mb-2">No hay tickets</h3>
          <p className="text-gray-500 mb-4">
            {searchText 
              ? 'No se encontraron tickets con ese criterio de búsqueda'
              : statusFilter === 'all' 
                ? 'Aún no has reportado ninguna incidencia'
                : `No hay tickets con estado "${statusConfig[statusFilter]?.label}"`}
          </p>
          {!searchText && (
            <Button onClick={() => navigate('/app/tickets/nuevo')}>
              <Plus className="w-4 h-4 mr-2" />
              Crear Primer Reporte
            </Button>
          )}
        </div>
      ) : (
        <div className="bg-white rounded-lg border overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Ticket</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Título</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Categoría</th>
                  <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Prioridad</th>
                  <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Estado</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Asignado a</th>
                  <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase" title="Tiempo de Asignación">TA</th>
                  <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase" title="Tiempo de Resolución">TR</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Fecha</th>
                  <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Acciones</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {filteredTickets.map((ticket) => {
                  const ta = getAssignmentTime(ticket);
                  const tr = getResolutionTime(ticket);
                  return (
                    <tr key={ticket.id} className="hover:bg-gray-50">
                      <td className="px-4 py-3 font-mono text-sm text-blue-600">{ticket.ticketNumber}</td>
                      <td className="px-4 py-3">
                        <div className="font-medium">{ticket.title}</div>
                        {ticket.location && (
                          <div className="text-xs text-gray-500">{ticket.location}</div>
                        )}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-600">{ticket.categoryName || '-'}</td>
                      <td className="px-4 py-3 text-center">
                        <span className={`inline-flex items-center gap-1 px-2 py-1 rounded text-xs font-medium ${priorityConfig[ticket.priority]?.color || 'bg-gray-100'}`}>
                          {priorityConfig[ticket.priority]?.icon}
                          {ticket.priority}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-center">
                        <span className={`px-2 py-1 rounded text-xs font-medium ${statusConfig[ticket.status]?.color || 'bg-gray-100'}`}>
                          {statusConfig[ticket.status]?.label || ticket.status}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-600">{ticket.assignedToName || '-'}</td>
                      <td className="px-4 py-3 text-center text-sm">
                        {ta ? (
                          <span className="text-blue-600 font-medium" title={`Asignado: ${ticket.assignedAt ? formatDate(ticket.assignedAt) : ''}`}>
                            {ta.formatted}
                          </span>
                        ) : (
                          <span className="text-gray-400">-</span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-center text-sm">
                        {tr ? (
                          <span className="text-green-600 font-medium" title={`Resuelto: ${ticket.resolvedAt ? formatDate(ticket.resolvedAt) : ''}`}>
                            {tr.formatted}
                          </span>
                        ) : (
                          <span className="text-gray-400">-</span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-500">{formatDate(ticket.createdAt)}</td>
                      <td className="px-4 py-3 text-center">
                        <Button 
                          variant="ghost" 
                          size="sm"
                          onClick={() => fetchTicketDetail(ticket.id)}
                        >
                          <Eye className="w-4 h-4" />
                        </Button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {selectedTicket && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-3xl max-h-[90vh] overflow-y-auto">
            {loadingDetail ? (
              <div className="text-center py-8 text-gray-500">Cargando detalle...</div>
            ) : (
              <>
                <div className="flex justify-between items-start mb-4">
                  <div>
                    <div className="text-sm text-gray-500 font-mono">{selectedTicket.ticketNumber}</div>
                    <h2 className="text-xl font-semibold">{selectedTicket.title}</h2>
                  </div>
                  <div className="flex gap-2">
                    <span className={`inline-flex items-center gap-1 px-2 py-1 rounded text-xs font-medium ${priorityConfig[selectedTicket.priority]?.color}`}>
                      {priorityConfig[selectedTicket.priority]?.icon}
                      {selectedTicket.priority}
                    </span>
                    <span className={`px-2 py-1 rounded text-xs font-medium ${statusConfig[selectedTicket.status]?.color}`}>
                      {statusConfig[selectedTicket.status]?.label}
                    </span>
                  </div>
                </div>

                <div className="space-y-4">
                  <div>
                    <label className="text-sm font-medium text-gray-500">Descripción</label>
                    <p className="mt-1 text-gray-700 whitespace-pre-wrap">{selectedTicket.description}</p>
                  </div>

                  <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                    <div>
                      <label className="text-sm font-medium text-gray-500">Categoría</label>
                      <p className="mt-1">{selectedTicket.categoryName || '-'}</p>
                    </div>
                    <div>
                      <label className="text-sm font-medium text-gray-500">Ubicación</label>
                      <p className="mt-1">{selectedTicket.location || selectedTicket.classroomName || '-'}</p>
                    </div>
                    <div>
                      <label className="text-sm font-medium text-gray-500">Equipo</label>
                      <p className="mt-1">{selectedTicket.equipmentName || '-'}</p>
                    </div>
                    <div>
                      <label className="text-sm font-medium text-gray-500">Asignado a</label>
                      <p className="mt-1">{selectedTicket.assignedToName || 'Sin asignar'}</p>
                    </div>
                    <div>
                      <label className="text-sm font-medium text-gray-500">Reportado por</label>
                      <p className="mt-1">{selectedTicket.reportedByName}</p>
                    </div>
                    <div>
                      <label className="text-sm font-medium text-gray-500">Fecha de Creación</label>
                      <p className="mt-1">{formatDate(selectedTicket.createdAt)}</p>
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4 p-3 bg-gray-50 rounded-lg">
                    <div>
                      <label className="text-sm font-medium text-gray-500">Tiempo de Asignación (TA)</label>
                      <p className="mt-1 text-lg font-semibold text-blue-600">
                        {getAssignmentTime(selectedTicket)?.formatted || 'Pendiente'}
                      </p>
                    </div>
                    <div>
                      <label className="text-sm font-medium text-gray-500">Tiempo de Resolución (TR)</label>
                      <p className="mt-1 text-lg font-semibold text-green-600">
                        {getResolutionTime(selectedTicket)?.formatted || 'Pendiente'}
                      </p>
                    </div>
                  </div>

                  {selectedTicket.comments && selectedTicket.comments.length > 0 && (
                    <div>
                      <label className="text-sm font-medium text-gray-500 mb-2 block">Historial de Actividad</label>
                      <div className="space-y-3 max-h-64 overflow-y-auto border rounded-lg p-3">
                        {selectedTicket.comments.map((comment) => (
                          <div 
                            key={comment.id} 
                            className={`p-3 rounded-lg ${
                              comment.commentType === 'status_change' ? 'bg-blue-50 border border-blue-100' :
                              comment.commentType === 'assignment' ? 'bg-yellow-50 border border-yellow-100' :
                              'bg-gray-50 border border-gray-100'
                            }`}
                          >
                            <div className="flex justify-between items-start text-xs text-gray-500 mb-1">
                              <span className="font-medium">{comment.userName}</span>
                              <span>{formatCommentDate(comment.createdAt)}</span>
                            </div>
                            <p className="text-sm text-gray-700 whitespace-pre-wrap">{comment.content}</p>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>

                <div className="flex justify-end mt-6">
                  <Button variant="outline" onClick={() => setSelectedTicket(null)}>
                    Cerrar
                  </Button>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
